import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchCountries, fetchEntities } from "../lib/dataFetchers";
import { CreateEntityModal } from "../components/CreateEntityModal";
import { useSession } from "../lib/auth";
import { canEdit as canEditRole } from "../lib/permissions";
import type { Region } from "../lib/types";
import { Button, FilterBar, FilterGroup, SegmentedControl, Select } from "../components/ui";
import { useDebounced } from "../lib/useDebounced";

export default function Entities(): JSX.Element {
  const { user } = useSession();
  const canEdit = canEditRole(user?.role);
  const { t } = useTranslation(["entities", "common", "directors"]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") ?? "");
  const [region, setRegion] = useState<Region | "ALL">("ALL");
  // "all" is the sentinel for "no country filter" — Radix Select forbids empty-string values.
  const [countryId, setCountryId] = useState<string>("all");
  const [createOpen, setCreateOpen] = useState(false);

  // Debounce search.
  const debouncedQ = useDebounced(q, 300);

  // World map drill-down deep-link: ?country=<name> resolves to a country UUID
  // once the countries list loads, populating the dropdown filter.
  const countryNameParam = searchParams.get("country");

  // state → URL — guard against writing the same value back, which would
  // re-fire the URL→state effect on the next render and start a loop.
  useEffect(() => {
    const current = searchParams.get("q") ?? "";
    if (debouncedQ === current) return;
    const next = new URLSearchParams(searchParams);
    if (debouncedQ) next.set("q", debouncedQ);
    else next.delete("q");
    setSearchParams(next, { replace: true });
  }, [debouncedQ, searchParams, setSearchParams]);

  // URL → state (header submit while already on /entities).
  useEffect(() => {
    const urlQ = searchParams.get("q") ?? "";
    if (urlQ !== q) setQ(urlQ);
    // q deliberately omitted: we only want to react to URL changes, not to our
    // own state writes (which the guard above already prevents from looping).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const { data: countries } = useQuery({ queryKey: ["countries"], queryFn: fetchCountries });

  // When ?country=<name> is present and the countries list has loaded,
  // resolve the name to an id once and seed the dropdown filter.
  // Guard treats "all" (sentinel) the same as unset, so deep-links still work.
  useEffect(() => {
    if (!countryNameParam || !countries?.length || (countryId && countryId !== "all")) return;
    const match = countries.find(
      (c) => c.name.toLowerCase() === countryNameParam.toLowerCase(),
    );
    if (match) setCountryId(match.id);
  }, [countryNameParam, countries, countryId]);

  const { data, isLoading } = useQuery({
    queryKey: ["entities", { q: debouncedQ, region, countryId }],
    queryFn: () =>
      fetchEntities({
        q: debouncedQ || undefined,
        region: region === "ALL" ? undefined : region,
        countryId: countryId && countryId !== "all" ? countryId : undefined,
        limit: 100,
      }),
  });

  const entities = data?.entities ?? [];

  // Build country select options.
  // "all" is the no-filter sentinel — Radix Select forbids empty-string values.
  // label is c.name only — the flag is passed separately so Select.tsx renders it
  // once via the explicit <span>; putting it in the label string would double-render.
  const countryOptions = [
    { value: "all", label: t("common:all_countries") },
    ...(countries ?? []).map((c) => ({
      value: c.id,
      label: c.name,
      flag: c.flag,
    })),
  ];

  // Region segmented control options
  const regionOptions: { value: Region | "ALL"; label: string }[] = [
    { value: "ALL",          label: t("common:all_regions") },
    { value: "EUROPE",       label: t("common:region_EUROPE") },
    { value: "MIDDLE_EAST",  label: "Middle East" },
    { value: "AFRICA",       label: "Africa" },
    { value: "NORTH_AMERICA",label: "North America" },
    { value: "LATIN_AMERICA",label: "Latin America" },
    { value: "ASIA_PACIFIC", label: "Asia-Pacific" },
  ];

  return (
    <div className="max-w-7xl mx-auto">
      {/* Page header — proto .page-h */}
      <div className="flex items-end justify-between gap-[14px] mb-[18px] flex-wrap">
        <div>
          <h1 className="text-[24px] leading-[1.1] m-0">{t("entities:page_title")}</h1>
          <p className="text-[13px] text-muted mt-[5px]">
            {t("entities:page_subtitle_other", { count: data?.total ?? entities.length })}
          </p>
        </div>
        {canEdit && (
          <Button
            variant="primary"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="h-[15px] w-[15px]" aria-hidden />
            {t("entities:create_button")}
          </Button>
        )}
      </div>

      {/* Filter bar — proto .filterbar */}
      <FilterBar>
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search
            className="absolute left-[11px] top-1/2 -translate-y-1/2 w-4 h-4 text-dim"
            aria-hidden
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            type="search"
            placeholder={t("common:search_placeholder_entities")}
            aria-label={t("common:search")}
            className="w-full h-9 pl-[35px] pr-3 border border-border rounded-lg bg-surface-2 text-[13.5px] focus:bg-surface focus:border-accent focus:ring-[3px] focus:ring-accent/[.14] focus:outline-none transition-[border-color,background,box-shadow]"
          />
        </div>

        {/* Region segmented control */}
        <FilterGroup label={t("common:filter")}>
          <SegmentedControl<Region | "ALL">
            options={regionOptions}
            value={region}
            onChange={setRegion}
            label={t("common:filter")}
          />
        </FilterGroup>

        {/* Country select */}
        <FilterGroup label={t("common:all_countries")}>
          <Select
            value={countryId}
            onValueChange={setCountryId}
            options={countryOptions}
            placeholder={t("common:all_countries")}
          />
        </FilterGroup>
      </FilterBar>

      {/* List */}
      {isLoading ? (
        <div className="grid gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="skeleton h-[52px]" />
          ))}
        </div>
      ) : entities.length === 0 ? (
        <div
          className="text-[13px] text-dim p-[18px] text-center border border-dashed border-border rounded-[9px] bg-surface"
        >
          <p>{t("entities:no_match")}</p>
          <button
            type="button"
            onClick={() => {
              setQ("");
              setRegion("ALL");
              setCountryId("all");
            }}
            className="mt-3 inline-flex min-h-[44px] sm:min-h-[34px] items-center justify-center rounded-lg border border-border bg-surface px-3 text-[12.5px] text-text hover:bg-surface-2 transition-colors"
          >
            {t("common:clear_filters")}
          </button>
        </div>
      ) : (
        <>
          {/* Desktop table — proto .panel .tbl-wrap .tbl .resp */}
          <div className="hidden md:block bg-surface border border-border rounded-[11px] shadow-card overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {[
                    t("entities:col_entity"),
                    t("entities:col_country"),
                    t("entities:col_legal_form"),
                    t("entities:col_director"),
                    t("entities:col_status"),
                  ].map((col, i) => (
                    <th
                      key={col}
                      className={[
                        "px-[15px] py-[11px] text-left",
                        "text-[11px] font-bold tracking-[.04em] uppercase text-dim",
                        "border-b border-border bg-surface sticky top-0 z-[1]",
                        i === 4 ? "text-right" : "",
                      ].join(" ")}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entities.map((e) => (
                  <Link
                    key={e.id}
                    to={`/entities/${e.id}`}
                    className="contents"
                    aria-label={`Open ${e.name}`}
                  >
                    <tr className="border-b border-border-2 last:border-b-0 hover:bg-surface-2 cursor-pointer transition-colors duration-[100ms]">
                      {/* Entity name */}
                      <td className="px-[15px] py-[13px] text-[13.5px] font-semibold text-text">
                        {e.name}
                      </td>
                      {/* Country with flag */}
                      <td className="px-[15px] py-[13px] text-[13.5px]">
                        {e.country?.flag && (
                          <span aria-hidden className="mr-2 text-[15px] leading-none">{e.country.flag}</span>
                        )}
                        {e.country?.name ?? "—"}
                      </td>
                      {/* Legal form */}
                      <td className="px-[15px] py-[13px] text-[13px] text-muted">
                        {e.legalForm ?? "—"}
                      </td>
                      {/* Director */}
                      <td className="px-[15px] py-[13px] text-[13.5px] text-text max-w-[140px] truncate">
                        {(() => {
                          const dirs = e.directors ?? [];
                          if (dirs.length === 0) return e.director ? <span>{e.director}</span> : <span className="text-dim">—</span>;
                          if (dirs.length === 1) return <span>{dirs[0].name}</span>;
                          return (
                            <span>
                              {dirs[0].name}
                              <span className="ml-1.5 inline-flex items-center rounded-full px-1.5 py-0 text-[10px] font-semibold bg-accent-soft text-accent border border-accent/20">
                                +{dirs.length - 1}
                              </span>
                            </span>
                          );
                        })()}
                      </td>
                      {/* Status pill */}
                      <td className="px-[15px] py-[13px] text-right">
                        <span className="inline-flex items-center gap-[5px] text-[11.5px] font-semibold px-[9px] py-0.5 rounded-full border bg-green-soft text-green-text border-[#a7f3d0] whitespace-nowrap">
                          <span aria-hidden className="w-[6px] h-[6px] rounded-full bg-green shrink-0" />
                          {e.status}
                        </span>
                      </td>
                    </tr>
                  </Link>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list — proto .resp stacked pattern */}
          <div className="md:hidden grid gap-[10px]">
            {entities.map((e) => (
              <Link
                key={e.id}
                to={`/entities/${e.id}`}
                className="block bg-surface border border-border rounded-[10px] shadow-card p-4 hover:shadow-card-hov transition-shadow"
              >
                <div className="flex items-start gap-3">
                  <span aria-hidden className="text-2xl shrink-0 leading-none mt-0.5">
                    {e.country?.flag ?? "🌐"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-[13.5px] font-semibold text-text truncate">{e.name}</h3>
                    <p className="text-[12px] text-muted truncate">
                      {e.country?.name ?? "—"} · {e.legalForm ?? "—"}
                    </p>
                    {(() => {
                      const dirs = e.directors ?? [];
                      if (dirs.length === 0 && !e.director) return null;
                      const firstName = dirs.length > 0 ? dirs[0].name : e.director;
                      const extra = dirs.length > 1 ? dirs.length - 1 : 0;
                      return (
                        <p className="text-[12px] text-dim truncate mt-0.5">
                          {firstName}
                          {extra > 0 && (
                            <span className="ml-1 inline-flex items-center rounded-full px-1.5 py-0 text-[10px] font-semibold bg-accent-soft text-accent border border-accent/20">
                              {t("directors:n_more", { count: extra })}
                            </span>
                          )}
                        </p>
                      );
                    })()}
                  </div>
                  <span className="inline-flex items-center gap-[5px] text-[11px] font-semibold px-[7px] py-0.5 rounded-full border bg-green-soft text-green-text border-[#a7f3d0] whitespace-nowrap shrink-0">
                    <span aria-hidden className="w-[5px] h-[5px] rounded-full bg-green" />
                    {e.status}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}

      <CreateEntityModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        countries={countries ?? []}
      />
    </div>
  );
}
